import React, { memo, useEffect, useState } from 'react';
import cn from 'classnames';
import { twMerge } from 'tailwind-merge';
import { Container, Element } from '@zmedia/bm-common/components';
import { RouterLink } from '@zmedia/bm-common/components/links';
import { Carousel } from '@zmedia/bm-common/components/swiper';
import Navbar from '@zmedia/bm-common/components/navbar';
import { UTILITIES_LIST } from '@zmedia/bm-common/modules/utilities/view/home/mock';

interface IProps {
    page?: string;
    classNames?: string;
    containerClass?: string;
}

function buildClassMenu(pathName?: string, path?: any) {
    if (pathName === undefined || path === undefined) {
        return false;
    }
    return pathName === path;
}

function buildItems(page: string, arr: any[]) {
    const _items = [...arr];
    const index = _items.findIndex((item) => item.shortName === page);
    const removedItem = _items.splice(index, 1)[0];
    arr = [removedItem, ..._items];
    return arr;
}

export function NavbarUtilityHeader({
    page = '',
    containerClass,
    classNames,
}: IProps) {
    const [items, setItems] = useState<any[]>([]);

    useEffect(() => {
        setItems(buildItems(page, [...UTILITIES_LIST]));
    }, []);

    return (
        <Element
            className={twMerge(
                'navbar-header',
                'relative mt-[86px]',
                classNames
            )}
        >
            <Container
                className={twMerge(
                    'pb-[16px] overflow-hidden',
                    'border-b border-solid border-[var(--black-10)]',
                    containerClass
                )}
            >
                <Navbar.Menu
                    className={cn(
                        'flex items-center h-full overflow-hidden mx-[-3px]',
                        'h-[26px]',
                        '[mask-image:linear-gradient(270deg,#fff0,#fffc_10%,#fff_25%,#fff)]',
                        '[--position-icon:-20px]'
                    )}
                >
                    <Tags page={page} items={items} />
                </Navbar.Menu>
            </Container>
        </Element>
    );
}

const Tags = React.memo(({ page, items }: { page: string; items: any[] }) => {
    const renderBoardItem = (item: any) => {
        const isActive = buildClassMenu(page, item?.shortName);
        return (
            <Navbar.Item
                key={`navbar.item.${item?.shortName}`}
                className={twMerge(
                    'flex items-center shrink-0',
                    'text-[1.4rem] leading-[1.67]',
                    'h-[26px]',
                    'rounded-full',
                    'bg-[#F4F4F5]',
                    'mx-[3px]',
                    cn({
                        ['bg-[rgba(47,161,179,0.2)] text-[var(--primary)]']:
                            isActive,
                    })
                )}
            >
                <UtilityItem {...item} />
            </Navbar.Item>
        );
    };

    return (
        <Carousel
            className={cn('!pr-[80px] [&_.swiper-slide]:w-auto')}
            data={items}
            itemRenderer={renderBoardItem}
            freeMode={true}
            itemsGap={0}
            itemsPerView="auto"
        />
    );
});

const UtilityItem = ({
    link,
    name,
    icon,
}: {
    link: string;
    name: string;
    icon: string;
}) => {
    let iconPosition = '';
    switch (icon) {
        case 'uti-12':
            iconPosition = `0 calc(11 * var(--position-icon))`;
            break;
        case 'uti-13':
            iconPosition = 'var(--position-icon) 0';
            break;
        default:
            iconPosition = `0 calc(${
                +icon.split('-')[1] - 1
            } * var(--position-icon))`;
    }

    if (icon === 'new-uti-1') {
        return (
            <RouterLink to={link} className={cn('px-[12px]')} title={name}>
                <Element
                    renderAs="i"
                    className={twMerge(
                        'inline-block',
                        'w-[20px] h-[20px]',
                        'align-top',
                        'bg-[url(https://baomoi-static.bmcdn.me/infoservice/styles/img/electric-sprite.png)]',
                        'bg-[length:60px_375px]',
                        'bg-[position:0_-70px]',
                        icon
                    )}
                />
                <Element renderAs="span" className={cn('ml-[6px]')}>
                    {name}
                </Element>
            </RouterLink>
        );
    }
    return (
        <RouterLink to={link} className={cn('px-[12px]')} title={name}>
            <Element
                renderAs="i"
                className={twMerge(
                    'inline-block',
                    'w-[20px] h-[20px]',
                    'align-top',
                    'bg-[url(https://baomoi-static.bmcdn.me/infoservice/styles/img/info-sprite-1.0.6.png)]',
                    'bg-[length:40px_1000px]',
                    icon
                )}
                style={{
                    backgroundPosition: iconPosition,
                }}
            />
            <Element renderAs="span" className={cn('ml-[6px]')}>
                {name}
            </Element>
        </RouterLink>
    );
};

export default memo(NavbarUtilityHeader);
